import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Gauge } from "lucide-react";
import { toast } from "sonner";
import { useCredits, refreshCredits } from "../lib/useCredits";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

export function CreditsWidget({ collapsed = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const state = useCredits();
  const warn80 = useRef(false);

  // Clic sur la jauge → Compte > Abonnement (aucun checkout automatique), scroll vers la section
  const location = useLocation();
  const goToPlans = () => {
    const scroll = () => document.querySelector('[data-testid="subscription-section"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (location.pathname === "/app/compte") scroll();
    else navigate("/app/compte#abonnement");
  };
  const clickable = "cursor-pointer transition-colors hover:border-electric/40";
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToPlans(); } };

  // Avertissement discret à 80 % (plans payants) — une seule fois par franchissement
  useEffect(() => {
    if (state?.mode === "quota" && state.warning && !state.exhausted && !warn80.current) {
      warn80.current = true;
      toast.info(t("usage.approaching"));
    }
    if (state?.mode === "quota" && !state.warning) warn80.current = false;
  }, [state, t]);

  if (!state) return null;
  const isQuota = state.mode === "quota";

  if (collapsed) {
    const label = isQuota
      ? t("usage.monthly", { pct: state.cycle_percent })
      : t("credits.remaining", { count: state.balance });
    return (
      <div className={`flex flex-col items-center gap-0.5 py-1 rounded-lg ${clickable} hover:bg-secondary`}
        data-testid="credits-counter" title={label} role="button" tabIndex={0}
        onClick={goToPlans} onKeyDown={onKey}>
        {isQuota
          ? <Gauge size={14} className={state.exhausted ? "text-destructive" : state.warning ? "text-amber-500" : "text-electric"} />
          : <Sparkles size={14} className={state.balance <= 5 ? "text-amber-500" : "text-electric"} />}
        <span className={`text-[9px] font-bold ${
          isQuota
            ? state.exhausted ? "text-destructive" : state.warning ? "text-amber-500" : "text-muted-foreground"
            : state.balance <= 5 ? "text-amber-500" : "text-muted-foreground"}`}>
          {isQuota ? `${state.cycle_percent}%` : state.balance}
        </span>
      </div>
    );
  }

  if (isQuota) {
    // Plans payants : jauge discrète, jamais de coût par action ni de compteur de crédits
    const barColor = state.exhausted ? "bg-destructive" : state.warning ? "bg-amber-500" : "bg-electric";
    const boxTone = state.exhausted ? "border-destructive/30 bg-destructive/5"
      : state.warning ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-secondary/40";
    return (
      <div className={`rounded-xl border px-3 py-2 ${boxTone} ${clickable}`} data-testid="credits-counter" data-mode="quota"
        role="button" tabIndex={0} onClick={goToPlans} onKeyDown={onKey}
        title={t("compte.subscription")}>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold">
            <Gauge size={12} className={state.exhausted ? "text-destructive" : state.warning ? "text-amber-500" : "text-electric"} />
            {t("usage.monthly", { pct: state.cycle_percent })}
          </span>
        </div>
        <div className="mt-1.5 h-1 rounded-full bg-border overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(100, state.cycle_percent)}%` }} />
        </div>
      </div>
    );
  }

  // Plan Gratuit : compteur de crédits visible (comportement voulu)
  const low = state.balance <= 5;
  const pct = Math.max(0, Math.min(100, (state.balance / (state.granted || 30)) * 100));
  return (
    <div className={`rounded-xl border px-3 py-2 ${low ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-secondary/40"} ${clickable}`}
      data-testid="credits-counter" data-mode="credits" role="button" tabIndex={0}
      onClick={goToPlans} onKeyDown={onKey} title={t("compte.subscription")}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold">
          <Sparkles size={12} className={low ? "text-amber-500" : "text-electric"} />
          {t("credits.remaining", { count: state.balance })}
        </span>
        <span className="text-[10px] text-muted-foreground">/ {state.granted}</span>
      </div>
      <div className="mt-1.5 h-1 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${low ? "bg-amber-500" : "bg-electric"}`}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function CreditsExhaustedDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const state = useCredits();

  useEffect(() => {
    const h = () => { refreshCredits(); setOpen(true); };
    window.addEventListener("credits:exhausted", h);
    return () => window.removeEventListener("credits:exhausted", h);
  }, []);

  const paid = state?.mode === "quota";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="credits-exhausted-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-electric" />
            {paid ? t("usage.reachedTitle") : t("credits.exhaustedTitle")}
          </DialogTitle>
          <DialogDescription>
            {paid ? t("usage.reachedDesc") : t("credits.exhaustedDesc")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <button onClick={() => setOpen(false)} data-testid="credits-dialog-later"
            className="rounded-full border border-border px-5 py-2 text-xs font-semibold hover:bg-secondary transition-colors">
            {t("credits.later")}
          </button>
          <button onClick={() => { setOpen(false); navigate("/app/compte"); }} data-testid="credits-dialog-plans"
            className="rounded-full bg-electric text-white px-5 py-2 text-xs font-bold hover:bg-blue-700 transition-colors">
            {t("credits.viewPlans")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
